import { computed, isSignal, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { map, startWith } from 'rxjs';

/**
 * Value-based dirty tracking for forms (NEX-499).
 *
 * Angular's `AbstractControl.dirty` flips to `true` on the first change and only
 * resets via `markAsPristine()` — it never goes back when the user reverts a value
 * to its original. These helpers instead compare the current value against an
 * original snapshot, so reverting a change makes a form clean again.
 *
 * Comparison rules (so callers can rely on them consistently):
 * - `null`, `undefined`, empty and whitespace-only strings are all equal.
 * - Strings are trimmed before comparison.
 * - Arrays are order-sensitive (reordering counts as a change).
 * - Object key order is irrelevant.
 * - `Date` values compare by their point in time.
 * - `Set` is treated as an order-sensitive sequence of its values.
 */

export interface DirtyTracker<T> {
  readonly isDirty: Signal<boolean>;
  reset(next: T): void;
}

export interface FormDirtyTracker<E = unknown> {
  readonly isDirty: Signal<boolean>;
  /**
   * Synchronous dirty check against the current live control value (and extra
   * state), bypassing the `valueChanges`-backed signal. Use this when the answer
   * is needed immediately inside a change handler, before signal propagation has
   * run (the `isDirty` signal may still hold the pre-change value at that point).
   */
  isDirtyNow(): boolean;
  reset(): void;
  /**
   * Takes a change that is already saved elsewhere into the baseline of the extra state, so it does not
   * count as unsaved while other unsaved changes stay dirty. `update` receives the baseline extra in its
   * normalized form (see {@link normalizeForComparison}).
   */
  updateBaselineExtra(update: (extra: E) => E): void;
  /** Extra state of the baseline in its normalized form; `null` without extra state. */
  readonly baselineExtra: Signal<E | null>;
}

export function normalizeForComparison(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value.getTime();
  if (value instanceof Set) return Array.from(value, normalizeForComparison);
  if (Array.isArray(value)) return value.map(normalizeForComparison);
  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      normalized[key] = normalizeForComparison(source[key]);
    }
    return normalized;
  }
  return value;
}

function canonical(value: unknown): string {
  return JSON.stringify(normalizeForComparison(value));
}

export function valuesEqual(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b);
}

export function createDirtyTracker<T>(current: Signal<T> | (() => T), initial: T): DirtyTracker<T> {
  const read = isSignal(current) ? current : (current as () => T);
  const baseline = signal(canonical(initial));
  const isDirty = computed(() => canonical(read()) !== baseline());
  return {
    isDirty,
    reset: (next: T) => baseline.set(canonical(next)),
  };
}

/**
 * Wires a reactive-forms control (plus optional signal-based side state such as
 * member lists or integration arrays) into a value-based dirty tracker.
 *
 * Must be called within an injection context (e.g. a component field initializer).
 * Call `reset()` after loading data and after a successful save to re-baseline
 * against the new server state.
 */
export function createFormDirtyTracker<E = unknown>(
  form: AbstractControl,
  options?: { extra?: () => E },
): FormDirtyTracker<E> {
  const extra = options?.extra;
  const formValue = toSignal(
    form.valueChanges.pipe(
      startWith(null),
      map(() => form.getRawValue()),
    ),
    {
      initialValue: form.getRawValue(),
    },
  );
  const snapshot = () => ({ form: formValue(), extra: extra ? extra() : null });
  const liveSnapshot = () => ({ form: form.getRawValue(), extra: extra ? extra() : null });

  const baseline = signal(canonical(liveSnapshot()));
  const isDirty = computed(() => canonical(snapshot()) !== baseline());
  const baselineExtra = computed(() => (JSON.parse(baseline()) as { extra: E | null }).extra);

  return {
    baselineExtra,
    isDirty,
    isDirtyNow: () => canonical(liveSnapshot()) !== baseline(),
    reset: () => baseline.set(canonical(liveSnapshot())),
    updateBaselineExtra: (update) => {
      const basis = JSON.parse(baseline()) as { form: unknown; extra: E };
      baseline.set(canonical({ form: basis.form, extra: update(basis.extra) }));
    },
  };
}
